import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { I18nProvider, useI18n } from "./i18n";
import { AuthProvider, WorkspaceProvider } from "./state";
import { PublicPage, AuthPage } from "./public";
import { Protected, WorkspaceChooser, Onboarding, Invitation } from "./setup";
import { Loading } from "./ui";
const Dashboard = lazy(() =>
  import("./workspace").then((module) => ({ default: module.Dashboard })),
);
const WorkspaceLayout = lazy(() =>
  import("./workspace").then((module) => ({ default: module.WorkspaceLayout })),
);
const Albums = lazy(() =>
  import("./content").then((module) => ({ default: module.Albums })),
);
const Photos = lazy(() =>
  import("./content").then((module) => ({ default: module.Photos })),
);
const PhotoDetail = lazy(() =>
  import("./content").then((module) => ({ default: module.PhotoDetail })),
);
const FamilyTree = lazy(() =>
  import("./tree").then((module) => ({ default: module.FamilyTree })),
);
const PersonDetail = lazy(() =>
  import("./tree").then((module) => ({ default: module.PersonDetail })),
);
const Stories = lazy(() =>
  import("./stories").then((module) => ({ default: module.Stories })),
);
const StoryDetail = lazy(() =>
  import("./stories").then((module) => ({ default: module.StoryDetail })),
);
const Settings = lazy(() =>
  import("./settings").then((module) => ({ default: module.Settings })),
);
const Trash = lazy(() =>
  import("./settings").then((module) => ({ default: module.Trash })),
);
import "./styles.css";
function NotFound() {
  const { t } = useI18n();
  return (
    <main className="not-found">
      <h1>{t("This page is not in the album.", "העמוד הזה אינו באלבום.")}</h1>
      <Link className="button" to="/app">
        {t("Return to your workspaces", "חזרה למרחבים שלכם")}
      </Link>
    </main>
  );
}
function App() {
  const { t } = useI18n();
  return (
    <>
      {import.meta.env.VITE_USE_EMULATORS !== "false" && (
        <div className="local-preview">
          {t(
            "Local preview — saved on this computer",
            "תצוגה מקומית — נשמרת במחשב זה",
          )}
        </div>
      )}
      <a className="skip-link" href="#main-content">
        {t("Skip to content", "דילוג לתוכן")}
      </a>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<PublicPage />} />
          <Route path="/he" element={<PublicPage />} />
          <Route path="/features" element={<PublicPage features />} />
          <Route path="/he/features" element={<PublicPage features />} />
          <Route path="/signup" element={<AuthPage signup />} />
          <Route path="/he/signup" element={<AuthPage signup />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/he/login" element={<AuthPage />} />
          <Route path="/invite" element={<Invitation />} />
          <Route element={<Protected />}>
            <Route path="/app" element={<WorkspaceChooser />} />
            <Route path="/app/new" element={<Onboarding />} />
            <Route path="/app/setup/:wid" element={<Onboarding />} />
            <Route
              path="/app/w/:wid"
              element={
                <WorkspaceProvider>
                  <WorkspaceLayout />
                </WorkspaceProvider>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="albums" element={<Albums />} />
              <Route path="albums/:albumId" element={<Photos />} />
              <Route path="photos" element={<Photos />} />
              <Route path="photos/:id" element={<PhotoDetail />} />
              <Route path="tree" element={<FamilyTree />} />
              <Route path="people/:id" element={<PersonDetail />} />
              <Route path="stories" element={<Stories />} />
              <Route path="stories/:id" element={<StoryDetail />} />
              <Route path="settings" element={<Settings />} />
              <Route path="trash" element={<Trash />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
